import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';

// Module-level cache to avoid duplicate requests when multiple components mount simultaneously
let cache = null;
let inFlight = null;

export function useVerifiedStatus() {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const [verified, setVerified] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchVerifiedStatus = useCallback(async () => {
    // Skip on force-reset-password page or if user must reset password
    if (location.pathname === '/force-reset-password' || user?.must_reset_password) {
      setLoading(false);
      return;
    }

    // Not authenticated — don't bother fetching
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }

    // Cache hit for this user
    if (cache && cache.userId === user.sub && cache.data !== null) {
      setVerified(cache.data);
      setLoading(false);
      return;
    }

    // In-flight request for this user — reuse it instead of making a duplicate
    if (inFlight && inFlight.userId === user.sub) {
      try {
        const data = await inFlight.promise;
        setVerified(data);
      } catch (e) {
        console.error('[useVerifiedStatus] Failed to fetch verified status:', e);
        setVerified(false);
      }
      setLoading(false);
      return;
    }

    // Fresh request
    const token = localStorage.getItem('token');
    const promise = fetch(`${process.env.REACT_APP_API_URL}/api/user/package-status`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => data?.verified === true);

    inFlight = { userId: user.sub, promise };

    try {
      const data = await promise;
      setVerified(data);
      cache = { userId: user.sub, data };
    } catch (e) {
      console.error('[useVerifiedStatus] Failed to fetch verified status:', e);
      setVerified(false);
    } finally {
      inFlight = null;
      setLoading(false);
    }
  }, [isAuthenticated, user, location.pathname]);

  useEffect(() => {
    fetchVerifiedStatus();
  }, [fetchVerifiedStatus]);

  return { verified: verified === true, loading };
}

// Clear the cache when user logs out/in (called by AuthContext on login/logout)
export function clearVerifiedStatusCache() {
  cache = null;
  inFlight = null;
}
